import React from 'react';
import { Box, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ActivityGroupActionTitle } from './ActivityGroupActionTitle';
import { AssignmentKey } from 'state/providers/LessonAssignmentProvider/types';
import { LessonAssignmentActivityCard } from './LessonAssignmentActivityCard';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';
import { useAlert, AlertVariant } from 'state/providers/AlertProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { BaseModalTitle } from 'components/@common/Modals/BaseModalTitle';
import { RemoveActivityModal } from 'components/@common/Modals/RemoveActivityModal';
import { AddActivityModal } from 'components/@common/Modals/AddActivitiesModal';
import { translate } from 'translations/translator';
import { getActivityId } from 'util/assignment-util';
import LessonAssignmentTranslationVariant from 'translations/variants/lessonAssignment';

const { activitySuccessfullyAddedTo, removeActivites, addActivity, addActivityTo } =
  LessonAssignmentTranslationVariant;

const getTranslation = (key: LessonAssignmentTranslationVariant, options?) =>
  translate('lessonAssignment', key, options);

type Props = {
  section:
    | {
        activities: Array<{
          id: number;
          lessonSectionId: number;
          type: { icon?: string; type: string };
          resource: { id: number; resourceTypeId: number; title: string };
          resourceId: number;
        }>;
        id: number;
        lessonId: number;
        title: string;
        sectionIdx: number;
      }
    | undefined;
};

export const LessonAssignmentActivityContent = ({ section }: Props) => {
  const [
    { lessonAssignmentState },
    { stepOneFormDispatch /* lessonAssignmentDispatch, stepperDispatch*/ }
  ] = useLessonAssignment();

  const classes = useStyles()();
  const [, { openAlert }] = useAlert();
  const [, { openModal }] = useModal();

  const handleRemoveAll = (sectionIdx: number | undefined, title) => () => {
    const handleOnRemove = () =>
      stepOneFormDispatch.onRemoveAllActivities(sectionIdx, AssignmentKey.LESSON);

    openModal({
      Title: () => <BaseModalTitle text={getTranslation(removeActivites)} />,
      body: <RemoveActivityModal activityName={title} handleOnRemove={handleOnRemove} />
    });
  };

  const handleRemove = (sectionIdx: number, activityId) => () =>
    stepOneFormDispatch.onRemoveActivity(
      sectionIdx,
      getActivityId(activityId),
      AssignmentKey.LESSON
    );

  const handleAddActivity = (sectionIdx, title) => () => {
    const onAddActivities = async activities => {
      await stepOneFormDispatch.onAddActivity(sectionIdx, activities);

      openAlert({
        body: `${getTranslation(activitySuccessfullyAddedTo)} ${title}`,
        variant: AlertVariant.SUCCESS
      });
    };

    openModal({
      Title: () => <BaseModalTitle text={`${getTranslation(addActivityTo)} ${title}`} />,
      body: <AddActivityModal handleAddActivity={onAddActivities} />
    });
  };

  const handleSwapData = activityLength => (originIndices, targetIndices) => {
    if (targetIndices.col >= 0 && targetIndices.item >= 0 && targetIndices.item < activityLength) {
      // const activity = stepOneFormState.form
      // todo get the activity based on index
      // get all the data needed to swap
      stepOneFormDispatch.onMoveActivity(originIndices, targetIndices);
      //setActiveFormData(swap(activeFormData, index, targetIndex));
    }
  };

  return (
    <Box className={`${classes.flex} ${classes.activityBorder}`}>
      <ActivityGroupActionTitle
        section={section}
        activityCount={section?.activities?.length}
        handleRemoveAll={handleRemoveAll(section?.sectionIdx, section?.title)}
        errorBtnClass={classes.errorBtn}
        className={`${classes.flex} ${classes.actionTitle}`}
      />
      {lessonAssignmentState.activeFormId && (
        <Box>
          {section?.activities?.map((activity: any, activityIdx) => (
            <LessonAssignmentActivityCard
              key={`activity-${activityIdx}`}
              handleSwap={handleSwapData(section?.activities.length)}
              activity={activity}
              index={activityIdx}
              item={section}
              col={section.sectionIdx}
              handleRemove={handleRemove(section?.sectionIdx, activity)}
            />
          ))}

          <Button
            className={classes.addActivityBtn}
            onClick={handleAddActivity(section?.sectionIdx, section?.title)}
          >
            <FontAwesomeIcon
              icon={faPlusCircle}
              size="sm"
              style={{ margin: '7px 15px 10px 8px' }}
            />
            {getTranslation(addActivity)}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const useStyles = () =>
  makeStyles(() => ({
    flex: {
      display: 'flex',
      flex: 1
    },
    errorBtn: {
      backgroundColor: '#D12348'
    },
    activityBorder: {
      border: '1px solid #DBDBDB',
      borderRadius: 5,
      flexDirection: 'column'
    },
    actionTitle: {
      justifyContent: 'space-between',
      margin: '32px 32px 0px 32px'
    },
    addActivityBtn: {
      height: '112px !important',
      display: 'flex',
      width: '-webkit-fill-available !important',
      border: '2px dashed #2878C1 !important',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '0 32px 24px 32px !important',
      textTransform: 'none !important',
      color: ' #2878C1 !important'
    } as any
  }));
